import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, RefreshCw, Save, Search, Merge, Star, ExternalLink } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { DEDUPE_CONFIG_URL, DEDUPE_GROUPS_URL, DEDUPE_MERGE_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { getStageBadgeClass } from "../../utils/leadStageBadge";
import { formatDate } from "../../utils/datetime";

/**
 * Settings -> Duplicate Rules (trustedge customers/dedupe.py).
 *
 * The switch and what it does: with it OFF every intake path creates a lead
 * exactly as before, only linked to the earlier lead on the same number so the
 * list can badge it. ON, an enquiry on a known number lands on that lead —
 * attached, or reopened if it was closed — instead of making a second one.
 *
 * Below it, every number that already has more than one lead, and a merge to
 * fold them into one.
 */

const RULES = [
    { key: 'on_open', label: 'Number belongs to an open lead',
      help: 'New Lead to Negotiation, and On Hold. The owner is told the person came back.' },
    { key: 'on_closed', label: 'Number belongs to a Dis-Qualified or Closed Lost lead',
      help: 'Most repeats on live data arrive after the earlier lead was Dis-Qualified.' },
    { key: 'on_customer', label: 'Number belongs to a customer (Closed Won / Onboarded)',
      help: 'Often a new site or an upgrade - a separate deal. The customer\'s owner gets the new lead.' },
    { key: 'reopen_owner', label: 'Who owns a reopened lead',
      help: 'An inactive previous owner always leaves it unassigned.' },
];

export default function DuplicateRules() {
    const { fetch, update, create } = useApiMethods();
    const navigate = useNavigate();

    const [cfg, setCfg]         = useState(null);
    const [draft, setDraft]     = useState(null);
    const [saving, setSaving]   = useState(false);
    const [groups, setGroups]   = useState([]);
    const [page, setPage]       = useState(1);
    const [pages, setPages]     = useState(1);
    const [total, setTotal]     = useState(0);
    const [search, setSearch]   = useState('');
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [pick, setPick]       = useState({});   // phone_key -> {keep, merge:Set}
    const [merging, setMerging] = useState(null);

    const loadConfig = useCallback(() => {
        fetch(DEDUPE_CONFIG_URL).then(res => {
            if (res?.status === 'success') { setCfg(res); setDraft(res); }
            else ToastMsg('error', res?.message || 'Could not load duplicate rules');
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadGroups = useCallback((pg = 1, q = search) => {
        setLoadingGroups(true);
        fetch(DEDUPE_GROUPS_URL, { page_no: pg, page_size: 15, search: q })
            .then(res => {
                if (res?.status === 'success') {
                    setGroups(res.groups || []); setPages(res.no_of_page || 1);
                    setTotal(res.total_groups || 0); setPage(res.page_no || pg);
                    const next = {};
                    (res.groups || []).forEach(g => {
                        next[g.phone_key] = {
                            keep: g.suggested_keep_id,
                            merge: new Set(g.leads.filter(l => l.id !== g.suggested_keep_id && !(l.blockers || []).length).map(l => l.id)),
                        };
                    });
                    setPick(next);
                } else if (res?.message) ToastMsg('error', res.message);
            })
            .finally(() => setLoadingGroups(false));
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadConfig(); loadGroups(1, ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const dirty = cfg && draft && ['is_enabled', 'on_open', 'on_closed', 'on_customer', 'reopen_owner',
        'require_reason', 'blocked_numbers'].some(k => cfg[k] !== draft[k]);

    const save = async () => {
        setSaving(true);
        try {
            const res = await update(DEDUPE_CONFIG_URL, {
                is_enabled: draft.is_enabled, on_open: draft.on_open, on_closed: draft.on_closed,
                on_customer: draft.on_customer, reopen_owner: draft.reopen_owner,
                require_reason: draft.require_reason, blocked_numbers: draft.blocked_numbers,
            });
            if (res?.status === 'success') { ToastMsg('success', res.message); setCfg(res); setDraft(res); }
            else ToastMsg('error', res?.message || 'Could not save');
        } finally { setSaving(false); }
    };

    const setKeep = (key, id) => setPick(p => {
        const g = groups.find(x => x.phone_key === key);
        return { ...p, [key]: { keep: id, merge: new Set(g.leads.filter(l => l.id !== id).map(l => l.id)) } };
    });
    const toggleMerge = (key, id) => setPick(p => {
        const m = new Set(p[key].merge);
        m.has(id) ? m.delete(id) : m.add(id);
        return { ...p, [key]: { ...p[key], merge: m } };
    });

    const merge = async (g) => {
        const sel = pick[g.phone_key];
        const ids = [...(sel?.merge || [])].filter(id => id !== sel.keep);
        if (!ids.length) { ToastMsg('error', 'Tick at least one lead to merge'); return; }
        const keepName = g.leads.find(l => l.id === sel.keep)?.customer_name || 'the kept lead';
        if (!window.confirm(`Merge ${ids.length} lead(s) into "${keepName}"?\n\nTheir activity, contacts, quotations, calls and tasks move to it, and they are removed from lists. This cannot be undone from the screen.`)) return;
        setMerging(g.phone_key);
        try {
            const res = await create(DEDUPE_MERGE_URL, { keep_id: sel.keep, merge_ids: ids });
            if (res?.status === 'success') { ToastMsg('success', res.message); loadGroups(page); loadConfig(); }
            else ToastMsg('error', res?.message || 'Merge failed');
        } finally { setMerging(null); }
    };

    if (!draft) {
        return <ContainerCard><div className="p-6 text-xs text-gray-400">Loading…</div></ContainerCard>;
    }
    const canEdit = cfg.can_edit;
    const choices = cfg.choices || {};

    return (
        <ContainerCard>
            <div className="p-4 space-y-6 overflow-auto">
                {/* ── header ── */}
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Copy className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Duplicate Rules</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                What happens when an enquiry arrives on a phone number the CRM already has.
                                Matched on the last 10 digits, against every lead and contact.
                            </p>
                        </div>
                    </div>
                    {canEdit && (
                        <button onClick={save} disabled={!dirty || saving}
                            className="h-8 px-3 rounded-md text-xs font-medium bg-primary hover:opacity-90 disabled:opacity-40 text-white flex items-center gap-1.5">
                            <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save'}
                        </button>
                    )}
                </div>

                {/* ── switch ── */}
                <div className={cn("rounded-xl border p-4 flex items-start justify-between gap-4",
                    draft.is_enabled ? "border-brand-200 bg-brand-50/60 dark:bg-brand-900/10 dark:border-brand-800/50"
                                     : "border-gray-200 dark:border-gray-700")}>
                    <div className="text-xs">
                        <p className="font-semibold text-gray-900 dark:text-white text-sm">
                            Route repeat enquiries to the existing lead {draft.is_enabled ? '- ON' : '- OFF'}
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
                            {draft.is_enabled
                                ? 'Meta / Zapier leads, Excel imports and the Add Lead form follow the rules below. The Add Lead form stops on a known number and offers to open it, add the enquiry to it, or create a new lead anyway. eMedHub referrals always get their own lead (rewards depend on it) and are linked.'
                                : 'Off: every path creates a new lead as it always did, linked to the earlier one so the list can badge it ("Same number", "Existing customer"). Nothing is attached or reopened automatically.'}
                        </p>
                    </div>
                    <button type="button" disabled={!canEdit}
                        onClick={() => setDraft({ ...draft, is_enabled: !draft.is_enabled })}
                        className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors",
                            draft.is_enabled ? "bg-primary" : "bg-gray-300 dark:bg-gray-600",
                            !canEdit && "opacity-50 cursor-not-allowed")}>
                        <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            draft.is_enabled ? "translate-x-6" : "translate-x-1")} />
                    </button>
                </div>

                {/* ── rules ── */}
                <div className={cn("grid gap-3 md:grid-cols-2", !draft.is_enabled && "opacity-60")}>
                    {RULES.map(r => (
                        <div key={r.key} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                            <label className="text-xs font-semibold text-gray-800 dark:text-gray-100">{r.label}</label>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">{r.help}</p>
                            <select value={draft[r.key]} disabled={!canEdit}
                                onChange={e => setDraft({ ...draft, [r.key]: e.target.value })}
                                className="w-full text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5">
                                {(choices[r.key] || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                        </div>
                    ))}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-800 dark:text-gray-100">
                            <input type="checkbox" checked={!!draft.require_reason} disabled={!canEdit}
                                onChange={e => setDraft({ ...draft, require_reason: e.target.checked })} />
                            Ask for a reason before "create a new lead anyway"
                        </label>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            On the Add Lead form. The reason is kept on the new lead's first enquiry.
                        </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                        <label className="text-xs font-semibold text-gray-800 dark:text-gray-100">Never match these numbers</label>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                            One per line - an agency's shared line, a front desk that fills in every form.
                            Dummy numbers like 1234567890 are already ignored.
                        </p>
                        <textarea rows={3} value={draft.blocked_numbers || ''} disabled={!canEdit}
                            onChange={e => setDraft({ ...draft, blocked_numbers: e.target.value })}
                            className="w-full text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 font-mono" />
                    </div>
                </div>

                {/* ── existing duplicates + merge ── */}
                <div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                                <Merge className="h-4 w-4 text-primary" /> Numbers with more than one lead
                                <span className="text-xs font-normal text-gray-500">· {total}</span>
                            </h3>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                Pick the lead to keep (★ suggested: an open lead being worked, else the customer).
                                Ticked leads are merged into it. Customers and leads with an MOU, onboarding,
                                CRE handover or accepted quotation can only be kept, never merged away.
                            </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1">
                                <Search className="h-3 w-3 text-gray-400" />
                                <input value={search} onChange={e => setSearch(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && loadGroups(1, search)}
                                    placeholder="Number or name…" className="text-xs bg-transparent outline-none w-36" />
                            </div>
                            <button onClick={() => loadGroups(page)} title="Reload"
                                className="h-7 w-7 rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 flex items-center justify-center">
                                <RefreshCw className={cn("h-3.5 w-3.5", loadingGroups && "animate-spin")} />
                            </button>
                        </div>
                    </div>

                    {!cfg.can_merge ? (
                        <p className="text-xs text-gray-400">Only admins can review and merge duplicates.</p>
                    ) : groups.length === 0 ? (
                        <p className="text-xs text-gray-400 py-4">{loadingGroups ? 'Loading…' : 'No duplicate numbers. '}</p>
                    ) : (
                        <div className="space-y-3">
                            {groups.map(g => {
                                const sel = pick[g.phone_key] || { keep: g.suggested_keep_id, merge: new Set() };
                                return (
                                    <div key={g.phone_key} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/40 px-3 py-1.5">
                                            <span className="text-xs font-mono font-semibold text-gray-700 dark:text-gray-200">
                                                {g.phone_key} <span className="font-sans font-normal text-gray-500">· {g.leads.length} leads</span>
                                            </span>
                                            <button onClick={() => merge(g)} disabled={merging === g.phone_key || !sel.merge.size}
                                                className="h-7 px-2.5 rounded-md text-[11px] font-medium bg-primary text-white disabled:opacity-40 flex items-center gap-1">
                                                <Merge className="h-3 w-3" /> {merging === g.phone_key ? 'Merging…' : `Merge ${sel.merge.size} into kept lead`}
                                            </button>
                                        </div>
                                        <table className="w-full text-xs">
                                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                                {g.leads.map(l => {
                                                    const isKeep = sel.keep === l.id;
                                                    const blocked = (l.blockers || []).length > 0 && !isKeep;
                                                    return (
                                                        <tr key={l.id} className={cn(isKeep && "bg-brand-50/60 dark:bg-brand-900/10")}>
                                                            <td className="px-3 py-2 w-16">
                                                                <label className="flex items-center gap-1 text-[10px] text-gray-500 cursor-pointer">
                                                                    <input type="radio" name={`keep-${g.phone_key}`} checked={isKeep}
                                                                        onChange={() => setKeep(g.phone_key, l.id)} /> Keep
                                                                </label>
                                                            </td>
                                                            <td className="px-2 py-2 w-16">
                                                                {!isKeep && (
                                                                    <label className={cn("flex items-center gap-1 text-[10px]", blocked ? "text-gray-300" : "text-gray-500 cursor-pointer")}
                                                                           title={blocked ? `Cannot merge: ${l.blockers.join('; ')}` : ''}>
                                                                        <input type="checkbox" disabled={blocked}
                                                                            checked={sel.merge.has(l.id)} onChange={() => toggleMerge(g.phone_key, l.id)} /> Merge
                                                                    </label>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-2">
                                                                <div className="flex items-center gap-1.5">
                                                                    {l.id === g.suggested_keep_id && <Star className="h-3 w-3 text-amber-500" title="Suggested to keep" />}
                                                                    <span className="font-medium text-gray-800 dark:text-gray-100">{l.customer_name || '—'}</span>
                                                                    {l.contact_name && l.contact_name !== l.customer_name &&
                                                                        <span className="text-gray-400">({l.contact_name})</span>}
                                                                </div>
                                                                {blocked && <p className="text-[10px] text-gray-400">{l.blockers[0]}</p>}
                                                            </td>
                                                            <td className="px-2 py-2">
                                                                <span className={cn("px-1.5 py-0.5 rounded-full text-[9px] font-medium", getStageBadgeClass(l.lead_stage))}>{l.lead_stage}</span>
                                                            </td>
                                                            <td className="px-2 py-2 text-gray-500">{l.owner || 'Unassigned'}</td>
                                                            <td className="px-2 py-2 text-gray-500">{l.lead_source || '—'}</td>
                                                            <td className="px-2 py-2 text-gray-500 whitespace-nowrap">{formatDate(l.created_date)}</td>
                                                            <td className="px-3 py-2 text-right">
                                                                <button onClick={() => navigate(`/customers/customerdetails?id=${l.id}`, { state: { formEdit: true, row: { id: l.id } } })}
                                                                    className="inline-flex items-center gap-0.5 text-[11px] text-primary hover:underline">
                                                                    Open <ExternalLink className="h-3 w-3" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })}
                            {pages > 1 && (
                                <div className="flex items-center justify-end gap-2 text-xs">
                                    <button disabled={page <= 1} onClick={() => loadGroups(page - 1)}
                                        className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Previous</button>
                                    <span className="text-gray-500">Page {page} of {pages}</span>
                                    <button disabled={page >= pages} onClick={() => loadGroups(page + 1)}
                                        className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Next</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </ContainerCard>
    );
}
